import { formatMoney } from '@theme/money-formatting';
import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

class CartAddUpsellModal extends DialogComponent {
  requiredRefs = ['dialog', 'image', 'productLink', 'productTitle', 'productVariant', 'productPrice', 'recommendations'];

  /** @type {AbortController | null} */
  #fetchAbortController = null;
  /** @type {(() => void) | null} */
  #carouselCleanup = null;
  #mobileMediaQuery = window.matchMedia('(max-width: 749px)');
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#fetchAbortController?.abort();
    this.#carouselCleanup?.();
  }

  /** @param {CartAddEvent} event */
  #handleCartAdd = async (event) => {
    if (this.#shouldSuppressModal(event)) return;
    if (event.detail.data?.didError) return;
    if (event.detail.data?.source !== 'product-form-component') return;

    const addedItem = event.detail.data?.addedItem;
    if (!addedItem) return;

    this.#populateAddedItem(addedItem);
    this.#closeOverlays();
    this.showDialog();
    await this.#loadRecommendations(addedItem);
  };

  /**
   * Decides whether to skip opening the upsell modal for a given add-to-cart
   * event. Excludes:
   *   1. ATC clicks INSIDE this same upsell modal — prevents the modal from
   *      reopening on top of itself when the user adds a recommended product.
   *   2. ATC clicks inside any element marked with `data-suppress-upsell-modal`
   *      — used by quick-add contexts that should add silently (predictive
   *      search dropdown, etc.) where the modal would be intrusive.
   *   3. The user is already on the cart template — the modal's whole purpose
   *      is "go to the cart" nudging. Showing it from inside the cart page
   *      (e.g., when adding via the cart-page recommendations carousel) is
   *      redundant and disruptive. `<main data-template="{{ template }}">` is
   *      rendered server-side in layout/theme.liquid so this check is reliable
   *      across SPA-style navigations.
   */
  #shouldSuppressModal(event) {
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) return false;

    if (eventTarget.closest('[data-suppress-upsell-modal]')) return true;

    const main = document.getElementById('MainContent');
    if (main?.dataset.template === 'cart') return true;

    return eventTarget.closest('cart-add-upsell-modal') === this;
  }

  showDialog() {
    if (this.#mobileMediaQuery.matches) {
      this.#showDialogMobile();
      return;
    }

    this.#showDialogDesktop();
  }

  closeDialog = async () => {
    if (this.#mobileMediaQuery.matches) {
      return this.#closeDialogMobile();
    }

    return this.#closeDialogDesktop();
  };

  #showDialogDesktop() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleMobileClick);
      this.addEventListener('keydown', this.#handleMobileKeyDown);
    });
  }

  #showDialogMobile() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.documentElement.setAttribute('scroll-lock', '');

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleMobileClick);
      this.addEventListener('keydown', this.#handleMobileKeyDown);
    });
  }

  async #closeDialogDesktop() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleMobileClick);
    this.removeEventListener('keydown', this.#handleMobileKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  }

  async #closeDialogMobile() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleMobileClick);
    this.removeEventListener('keydown', this.#handleMobileKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.documentElement.removeAttribute('scroll-lock');

    dialog.close();
    dialog.classList.remove('dialog-closing');
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    this.dispatchEvent(new DialogCloseEvent());
  }

  #handleMobileClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  #handleMobileKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  #closeOverlays() {
    const quickAddDialog = document.getElementById('quick-add-dialog');
    if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
      quickAddDialog.closeDialog();
    }

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.closeDialog === 'function') {
      cartDrawer.closeDialog();
    }
  }

  /**
   * @param {Record<string, any>} addedItem
   */
  #populateAddedItem(addedItem) {
    const productUrl = addedItem.url || addedItem.product_url || '#';
    const imageUrl =
      addedItem.featured_image?.url ||
      addedItem.image ||
      addedItem.featured_media?.preview_image?.src ||
      '';
    const productTitle = addedItem.product_title || addedItem.title || '';
    const variantTitle = addedItem.variant_title && addedItem.variant_title !== 'Default Title' ? addedItem.variant_title : '';
    const price = addedItem.final_price ?? addedItem.price ?? null;

    this.refs.productLink.href = productUrl;
    this.refs.productTitle.textContent = productTitle;
    this.refs.productVariant.textContent = variantTitle;
    this.refs.productVariant.hidden = !variantTitle;

    if (imageUrl) {
      this.refs.image.src = imageUrl;
      this.refs.image.alt = productTitle;
      this.refs.image.hidden = false;
    } else {
      this.refs.image.hidden = true;
      this.refs.image.removeAttribute('src');
    }

    if (typeof price === 'number') {
      this.refs.productPrice.textContent = formatMoney(
        price,
        this.dataset.moneyFormat || '${{amount}}',
        this.dataset.currency || 'USD'
      );
      this.refs.productPrice.hidden = false;
    } else {
      this.refs.productPrice.hidden = true;
      this.refs.productPrice.textContent = '';
    }
  }

  /**
   * @param {Record<string, any>} addedItem
   */
  async #loadRecommendations(addedItem) {
    const productId = addedItem.product_id || addedItem.product?.id;
    if (!productId) {
      this.refs.recommendations.innerHTML = '';
      return;
    }

    this.refs.recommendations.setAttribute('aria-busy', 'true');
    this.refs.recommendations.innerHTML = this.#loadingMarkup();

    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    const url = new URL(this.dataset.recommendationsUrl || window.location.origin, window.location.origin);
    url.searchParams.set('section_id', 'cart-add-upsell-products');
    url.searchParams.set('product_id', String(productId));
    url.searchParams.set('limit', this.dataset.maxProducts || '4');
    url.searchParams.set('intent', this.dataset.recommendationIntent || 'complementary');

    try {
      const response = await fetch(url.toString(), { signal: this.#fetchAbortController.signal });
      if (!response.ok) throw new Error(`Failed to load upsell recommendations: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-cart-add-upsell-products]');
      const isEmpty = html.querySelector('[data-cart-add-upsell-products-empty]');

      this.refs.recommendations.innerHTML = content?.outerHTML || '';
      this.refs.recommendations.closest('.cart-add-upsell-modal__recommendations-panel')?.toggleAttribute('hidden', !content || !!isEmpty);

      if (content && !isEmpty) {
        this.#initCarousel();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
        this.refs.recommendations.innerHTML = '';
        this.refs.recommendations.closest('.cart-add-upsell-modal__recommendations-panel')?.setAttribute('hidden', '');
      }
    } finally {
      this.refs.recommendations.removeAttribute('aria-busy');
      this.#fetchAbortController = null;
    }
  }

  #loadingMarkup() {
    const count = Number(this.dataset.maxProducts || 4);
    return `
      <div class="cart-add-upsell-products cart-add-upsell-products--loading" data-cart-add-upsell-products>
        <div class="cart-add-upsell-products__carousel">
          <div class="cart-add-upsell-products__viewport">
            <div class="cart-add-upsell-products__list">
              <div class="cart-add-upsell-products__leading-space" aria-hidden="true"></div>
              ${Array.from({ length: count })
                .map(
                  () => `
                    <div class="cart-add-upsell-products__item">
                      <div class="cart-add-upsell-products__card cart-add-upsell-products__card--skeleton">
                        <div class="cart-add-upsell-products__image-link cart-add-upsell-products__image-link--skeleton">
                          <div class="cart-add-upsell-products__skeleton cart-add-upsell-products__skeleton--image"></div>
                        </div>
                        <div class="cart-add-upsell-products__info">
                          <div class="cart-add-upsell-products__skeleton cart-add-upsell-products__skeleton--title"></div>
                          <div class="cart-add-upsell-products__skeleton cart-add-upsell-products__skeleton--title cart-add-upsell-products__skeleton--title-short"></div>
                          <div class="cart-add-upsell-products__bottom">
                            <div class="cart-add-upsell-products__price cart-add-upsell-products__price--skeleton">
                              <div class="cart-add-upsell-products__skeleton cart-add-upsell-products__skeleton--compare-price"></div>
                              <div class="cart-add-upsell-products__skeleton cart-add-upsell-products__skeleton--price"></div>
                            </div>
                            <div class="cart-add-upsell-products__skeleton cart-add-upsell-products__skeleton--button"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  `
                )
                .join('')}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  #initCarousel() {
    this.#carouselCleanup?.();
    this.#carouselCleanup = null;

    const carousel = this.refs.recommendations.querySelector('[data-upsell-carousel]');
    if (!(carousel instanceof HTMLElement)) return;

    const viewport = carousel.querySelector('[data-upsell-carousel-viewport]');
    const slides = Array.from(carousel.querySelectorAll('[data-upsell-carousel-slide]'));
    const prevButton = carousel.querySelector('[data-upsell-carousel-prev]');
    const nextButton = carousel.querySelector('[data-upsell-carousel-next]');
    const dotsContainer = carousel.querySelector('[data-upsell-carousel-dots]');
    const controls = carousel.querySelector('[data-upsell-carousel-controls]');
    const list = carousel.querySelector('.cart-add-upsell-products__list');
    const leadingSpace = carousel.querySelector('.cart-add-upsell-products__leading-space');

    if (!(viewport instanceof HTMLElement) || slides.length <= 1) {
      controls?.setAttribute('hidden', '');
      return;
    }

    let dots = [];
    let scrollTimer = 0;
    let setupTimer = 0;
    let resizeObserver = null;
    /** @type {number[]} */
    let snapPoints = [];

    const getActivePage = () => {
      if (!snapPoints.length) return 0;

      const currentScroll = viewport.scrollLeft;
      let activePage = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      snapPoints.forEach((point, index) => {
        const distance = Math.abs(point - currentScroll);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          activePage = index;
        }
      });

      return activePage;
    };

    const updateState = () => {
      const currentPage = getActivePage();
      const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const firstSnapPoint = snapPoints[0] ?? 0;
      const isAtStart = viewport.scrollLeft <= firstSnapPoint + 2;
      const isAtEnd = viewport.scrollLeft >= maxScrollLeft - 2;

      dots.forEach((dot, index) => {
        if (index === currentPage) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (prevButton instanceof HTMLButtonElement) {
        prevButton.disabled = isAtStart;
      }

      if (nextButton instanceof HTMLButtonElement) {
        nextButton.disabled = isAtEnd;
      }
    };

    const setup = () => {
      const pageWidth = viewport.clientWidth || 0;
      const maxScrollLeft = Math.max(0, viewport.scrollWidth - pageWidth);

      if (pageWidth <= 0) return;

      const firstSlide = slides[0];
      const secondSlide = slides[1];

      if (!(firstSlide instanceof HTMLElement)) return;

      const slideWidth = firstSlide.getBoundingClientRect().width || firstSlide.offsetWidth || 0;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const computedSnapPoints = [];
      const fullCardsPerPage = isMobile ? 2 : 3;
      const lastSlide = slides[slides.length - 1];
      const desiredLastSnapPoint =
        lastSlide instanceof HTMLElement
          ? Math.max(0, Math.min(lastSlide.offsetLeft + lastSlide.offsetWidth - pageWidth, maxScrollLeft))
          : maxScrollLeft;

      for (let index = 0; index < slides.length; index += fullCardsPerPage) {
        const slide = slides[index];
        if (!(slide instanceof HTMLElement)) continue;

        const candidate = Math.max(0, Math.min(slide.offsetLeft, desiredLastSnapPoint));
        computedSnapPoints.push(candidate);

        if (candidate >= desiredLastSnapPoint - 2) break;
      }

      if (!computedSnapPoints.length) {
        computedSnapPoints.push(Math.min(firstSlide.offsetLeft, desiredLastSnapPoint));
      }

      const lastSnapPoint = computedSnapPoints[computedSnapPoints.length - 1] ?? 0;
      if (desiredLastSnapPoint > lastSnapPoint + 2) {
        computedSnapPoints.push(desiredLastSnapPoint);
      } else {
        computedSnapPoints[computedSnapPoints.length - 1] = desiredLastSnapPoint;
      }

      snapPoints = [...new Set(computedSnapPoints.map((point) => Math.max(0, Math.min(point, maxScrollLeft))))];

      const showControls = snapPoints.length > 1;

      if (!showControls) {
        controls?.setAttribute('hidden', '');
        dotsContainer?.replaceChildren();
        dots = [];
        updateState();
        return;
      }

      controls?.removeAttribute('hidden');
      dotsContainer?.replaceChildren();
      dots = Array.from({ length: snapPoints.length }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cart-add-upsell-products__dot';
        dot.setAttribute('aria-label', `Go to recommendation page ${index + 1}`);
        dot.addEventListener('click', () => {
          viewport.scrollTo({ left: snapPoints[index], behavior: 'smooth' });
        });
        dotsContainer?.appendChild(dot);
        return dot;
      });

      updateState();
    };

    const handlePrevClick = () => {
      const currentPage = getActivePage();
      const targetPage = Math.max(0, currentPage - 1);
      const targetScrollLeft = snapPoints[targetPage] ?? 0;

      viewport.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    };

    const handleNextClick = () => {
      const currentPage = getActivePage();
      const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const nextPage = currentPage + 1;
      const nextSnapPoint = snapPoints[nextPage];
      const canReachEnd = viewport.scrollLeft < maxScrollLeft - 2;

      viewport.scrollTo(
        { left: nextSnapPoint ?? (canReachEnd ? maxScrollLeft : viewport.scrollLeft), behavior: 'smooth' }
      );
    };

    const handleScroll = () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(updateState, 80);
    };

    const handleResize = () => {
      window.clearTimeout(setupTimer);
      setupTimer = window.setTimeout(() => {
        window.requestAnimationFrame(setup);
      }, 40);
    };

    prevButton?.addEventListener('click', handlePrevClick);
    nextButton?.addEventListener('click', handleNextClick);
    viewport.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => handleResize());
      resizeObserver.observe(viewport);
      if (list instanceof HTMLElement) {
        resizeObserver.observe(list);
      }
    }

    window.requestAnimationFrame(() => {
      setup();
      window.setTimeout(setup, 80);
      window.setTimeout(setup, 180);
    });

    this.#carouselCleanup = () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(setupTimer);
      prevButton?.removeEventListener('click', handlePrevClick);
      nextButton?.removeEventListener('click', handleNextClick);
      viewport.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }
}

if (!customElements.get('cart-add-upsell-modal')) {
  customElements.define('cart-add-upsell-modal', CartAddUpsellModal);
}
